import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import store from '../Store';

interface PaymentSubscription {
  id: string;
  user_id: string;
  package_id: string;
  payment_method: string;
  transaction_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_date: string;
  subscription_start_date: string;
  subscription_end_date: string;
  is_published: boolean;
  published_at?: string;
  renewal_notification_sent: boolean;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    first_name: string;
    first_name_ar: string;
    last_name: string;
    last_name_ar: string;
    email: string;
    image_path?: string;
  };
  package: {
    id: string;
    name: string;
    description: string;
    price: number;
    duration_days: number;
  };
}

const PaymentSubscriptionsPage: React.FC = observer(() => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');
  const [selectedSubscription, setSelectedSubscription] = useState<PaymentSubscription | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [serviceData, setServiceData] = useState({
    name: '',
    description: '',
    category_id: '',
    city_id: '',
    website: '',
    longitude: '',
    latitude: '',
    certificate_path: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [subscriptions, setSubscriptions] = useState<PaymentSubscription[]>([]);

  useEffect(() => {
    fetchPaymentSubscriptions();
  }, []);

  const fetchPaymentSubscriptions = async () => {
    try {
      setIsProcessing(true);
      const response = await fetch('/api/payment-subscriptions', {
        headers: {
          'Authorization': `Bearer ${store.token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching payment subscriptions:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesSearch = 
      subscription.user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.user.first_name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || subscription.status === filterStatus;
    const matchesPublished = filterPublished === 'all' || 
      (filterPublished === 'published' && subscription.is_published) ||
      (filterPublished === 'unpublished' && !subscription.is_published);
    
    return matchesSearch && matchesStatus && matchesPublished;
  });

  const handlePublishService = async () => {
    if (!selectedSubscription || !serviceData.name.trim()) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    try {
      setIsProcessing(true);
      const response = await fetch(`/api/payment-subscriptions/${selectedSubscription.id}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${store.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ service_data: serviceData }),
      });
      
      if (response.ok) {
        await fetchPaymentSubscriptions();
        setShowPublishModal(false);
        setServiceData({
          name: '',
          description: '',
          category_id: '',
          city_id: '',
          website: '',
          longitude: '',
          latitude: '',
          certificate_path: ''
        });
        setSelectedSubscription(null);
        alert('تم نشر المهنة بنجاح');
      } else {
        alert('حدث خطأ في نشر المهنة');
      }
    } catch (error) {
      console.error('Error publishing service:', error);
      alert('حدث خطأ في نشر المهنة');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'refunded':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'في الانتظار';
      case 'completed':
        return 'مكتمل';
      case 'failed':
        return 'فشل';
      case 'refunded':
        return 'مسترد';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return '⏳';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'refunded':
        return '🔄';
      default:
        return '❓';
    }
  };

  const isExpired = (endDate: string) => {
    return new Date(endDate) < new Date();
  };

  const needsRenewal = (endDate: string) => {
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
    return new Date(endDate) <= oneMonthFromNow;
  };

  if (isProcessing && subscriptions.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate statistics
  const totalSubscriptions = subscriptions.length;
  const completedSubscriptions = subscriptions.filter(sub => sub.status === 'completed').length;
  const publishedSubscriptions = subscriptions.filter(sub => sub.is_published).length;
  const expiredSubscriptions = subscriptions.filter(sub => isExpired(sub.subscription_end_date)).length;
  const renewalNeeded = subscriptions.filter(sub => needsRenewal(sub.subscription_end_date)).length;

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">إدارة المدفوعات والاشتراكات</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">إدارة المدفوعات الناجحة ونشر المهن</p>
          </div>
          <div className="flex items-center space-x-4 space-x-reverse">
            <button
              onClick={fetchPaymentSubscriptions}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2 space-x-reverse"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>تحديث</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">إجمالي الاشتراكات</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalSubscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">مكتملة</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{completedSubscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">منشورة</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{publishedSubscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-red-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">منتهية</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{expiredSubscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 border-l-4 border-yellow-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 1 0-15 0v5h5l-5 5-5-5h5v-5a7.5 7.5 0 1 0 15 0v5z" />
                </svg>
              </div>
            </div>
            <div className="mr-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">تحتاج تجديد</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{renewalNeeded}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              البحث
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="البحث في الاشتراكات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              حالة الدفع
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">جميع الحالات</option>
              <option value="pending">في الانتظار</option>
              <option value="completed">مكتمل</option>
              <option value="failed">فشل</option>
              <option value="refunded">مسترد</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              حالة النشر
            </label>
            <select
              value={filterPublished}
              onChange={(e) => setFilterPublished(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">جميع الحالات</option>
              <option value="published">منشورة</option>
              <option value="unpublished">غير منشورة</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
                setFilterPublished('all');
              }}
              className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>
      </div>

      {/* Subscriptions List */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">قائمة الاشتراكات</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">إجمالي {filteredSubscriptions.length} اشتراك</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  رقم الاشتراك
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  المستخدم
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  الباقة
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  المبلغ
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  حالة الدفع
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  حالة النشر
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  تاريخ الانتهاء
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    <div className="flex items-center">
                      <span className="text-lg">{getStatusIcon(subscription.status)}</span>
                      <span className="mr-2">#{subscription.id}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {subscription.user.image_path && (
                        <img className="h-10 w-10 rounded-full ml-3" src={subscription.user.image_path} alt="" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {subscription.user.first_name_ar || subscription.user.first_name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {subscription.user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div>
                      <div className="font-medium">{subscription.package.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {subscription.package.duration_days} يوم
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">{subscription.amount} {subscription.currency}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                      {getStatusText(subscription.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                      subscription.is_published 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {subscription.is_published ? 'منشورة' : 'غير منشورة'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div>
                      <div>{new Date(subscription.subscription_end_date).toLocaleDateString('ar-SA')}</div>
                      {isExpired(subscription.subscription_end_date) && (
                        <div className="text-xs text-red-500">منتهية</div>
                      )}
                      {needsRenewal(subscription.subscription_end_date) && !isExpired(subscription.subscription_end_date) && (
                        <div className="text-xs text-yellow-500">تحتاج تجديد</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2 space-x-reverse">
                      <button
                        onClick={() => {
                          setSelectedSubscription(subscription);
                          setShowDetailsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 px-3 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors duration-200"
                        title="عرض التفاصيل"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      {subscription.status === 'completed' && !subscription.is_published && (
                        <button
                          onClick={() => {
                            setSelectedSubscription(subscription);
                            setShowPublishModal(true);
                          }}
                          disabled={isProcessing}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 px-3 py-1 rounded-md hover:bg-green-50 dark:hover:bg-green-900 transition-colors duration-200 disabled:opacity-50"
                          title="نشر المهنة"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredSubscriptions.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">لا توجد اشتراكات</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">لا توجد اشتراكات مطابقة للبحث</p>
          </div>
        )}
      </div>

      {/* Subscription Details Modal */}
      {showDetailsModal && selectedSubscription && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-xl bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  تفاصيل الاشتراك #{selectedSubscription.id}
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      رقم الاشتراك
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white font-mono">#{selectedSubscription.id}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      المستخدم
                    </label>
                    <div className="flex items-center">
                      {selectedSubscription.user.image_path && (
                        <img className="h-8 w-8 rounded-full ml-2" src={selectedSubscription.user.image_path} alt="" />
                      )}
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedSubscription.user.first_name_ar || selectedSubscription.user.first_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {selectedSubscription.user.email}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      الباقة
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white font-medium">{selectedSubscription.package.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedSubscription.package.duration_days} يوم
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      المبلغ
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white font-medium">
                      {selectedSubscription.amount} {selectedSubscription.currency}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      حالة الدفع
                    </label>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedSubscription.status)}`}>
                      {getStatusText(selectedSubscription.status)}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      حالة النشر
                    </label>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                      selectedSubscription.is_published 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {selectedSubscription.is_published ? 'منشورة' : 'غير منشورة'}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      تاريخ البداية
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(selectedSubscription.subscription_start_date).toLocaleDateString('ar-SA')}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      تاريخ الانتهاء
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(selectedSubscription.subscription_end_date).toLocaleDateString('ar-SA')}
                    </p>
                    {isExpired(selectedSubscription.subscription_end_date) && (
                      <p className="text-xs text-red-500">منتهية</p>
                    )}
                    {needsRenewal(selectedSubscription.subscription_end_date) && !isExpired(selectedSubscription.subscription_end_date) && (
                      <p className="text-xs text-yellow-500">تحتاج تجديد</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 space-x-reverse mt-8">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Service Modal */}
      {showPublishModal && selectedSubscription && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-xl bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  نشر المهنة للاشتراك #{selectedSubscription.id}
                </h3>
                <button
                  onClick={() => {
                    setShowPublishModal(false);
                    setServiceData({
                      name: '',
                      description: '',
                      category_id: '',
                      city_id: '',
                      website: '',
                      longitude: '',
                      latitude: '',
                      certificate_path: ''
                    });
                    setSelectedSubscription(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    اسم المهنة *
                  </label>
                  <input
                    type="text"
                    value={serviceData.name}
                    onChange={(e) => setServiceData({...serviceData, name: e.target.value})}
                    placeholder="اسم المهنة"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    وصف المهنة *
                  </label>
                  <textarea
                    value={serviceData.description}
                    onChange={(e) => setServiceData({...serviceData, description: e.target.value})}
                    placeholder="وصف المهنة"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الموقع الإلكتروني
                    </label>
                    <input
                      type="url"
                      value={serviceData.website}
                      onChange={(e) => setServiceData({...serviceData, website: e.target.value})}
                      placeholder="https://example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      مسار الشهادة
                    </label>
                    <input
                      type="text"
                      value={serviceData.certificate_path}
                      onChange={(e) => setServiceData({...serviceData, certificate_path: e.target.value})}
                      placeholder="مسار الشهادة"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      خط الطول
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={serviceData.longitude}
                      onChange={(e) => setServiceData({...serviceData, longitude: e.target.value})}
                      placeholder="خط الطول"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      خط العرض
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={serviceData.latitude}
                      onChange={(e) => setServiceData({...serviceData, latitude: e.target.value})}
                      placeholder="خط العرض"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 space-x-reverse mt-8">
                <button
                  onClick={() => {
                    setShowPublishModal(false);
                    setServiceData({
                      name: '',
                      description: '',
                      category_id: '',
                      city_id: '',
                      website: '',
                      longitude: '',
                      latitude: '',
                      certificate_path: ''
                    });
                    setSelectedSubscription(null);
                  }}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors duration-200"
                >
                  إلغاء
                </button>
                <button
                  onClick={handlePublishService}
                  disabled={isProcessing || !serviceData.name.trim() || !serviceData.description.trim()}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isProcessing ? 'جاري النشر...' : 'نشر المهنة'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PaymentSubscriptionsPage;
